import React, { Suspense } from 'react';
import ReactDOM from "react-dom/client";
import "./index.scss";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider, } from '@tanstack/react-query'
import OrgIdContextProvider from "./store/OrgIdContextProvider";
import { ThemeProvider } from "./store/ThemeContext";
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import translationEN from './locales/en/translation.json';
import translationSI from './locales/si/translation.json';
import translationDE from './locales/de/translation.json';
import translationRS from './locales/rs/translation.json';
import translationME from './locales/me/translation.json';
import translationHR from './locales/hr/translation.json';
import translationMK from './locales/mk/translation.json';
import translationIT from './locales/it/translation.json';
import translationPL from './locales/pl/translation.json';
import translationRO from './locales/ro/translation.json';
import translationNL from './locales/nl/translation.json';
import translationEE from './locales/ee/translation.json';
import translationSK from './locales/sk/translation.json';
import translationCZ from './locales/cz/translation.json';
import translationFR from './locales/fr/translation.json';
import translationES from './locales/es/translation.json';
import translationCA from './locales/ca/translation.json';
import translationBG from './locales/bg/translation.json';
import translationPT from './locales/pt/translation.json';
import { WebsocketProvider } from './store/WebsocketContext';
import BasepathContextProvider from './store/BasepathContextProvider';
import { E2Data, brandPostProcessor } from './lib/ddd/ddd';
const IntegrationApp = React.lazy(() => import('./IntegrationApp'));

const apiRequestAddress = process.env.REACT_APP_API_ENDPOINT || window.location.origin + "/json.rpc";
const root = ReactDOM.createRoot(document.getElementById("root"));
//locales: ['en', "si", 'de',"rs","me","hr","mk","it","pl","ro","nl", "ee","sk", "cz", 'fr', 'es'], 
// the translations
const resources = {
  en: {
    translation: translationEN
  },
  si: {
    translation: translationSI
  },
  de: {
    translation: translationDE
  },
  rs: {
    translation: translationRS
  },
  me: {
    translation: translationME
  },
  hr: {
    translation: translationHR
  },
  mk: {
    translation: translationMK
  },
  it: {
    translation: translationIT
  },
  pl: {
    translation: translationPL
  },
  ro: {
    translation: translationRO
  },
  nl: {
    translation: translationNL
  },
  ee: {
    translation: translationEE
  },
  sk: {
    translation: translationSK
  },
  cz: {
    translation: translationCZ
  },
  fr: {
    translation: translationFR
  },
  es: {
    translation: translationES
  },
  ca: {
    translation: translationCA
  },
  bg: {
    translation: translationBG
  },
  pt: {
    translation: translationPT
  },  
};


i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .use(brandPostProcessor)
  .init({
    //resources,
    fallbackLng: 'en',
    debug: false,
    //lng: "en",
    postProcess: ['brandReplacer'],
    interpolation: {
      escapeValue: false,
    },
    backend: {
      //loadPath: apiRequestAddress,
      loadPath: '{{lng}}',
      request: (options, url, payload, callback) => {
/*         console.log(options);
        console.log(url);
        console.log(payload);
 */
        if (!url || url === "en" || url === "us") {
          console.log("Skipping server fetch for 'en', using default keys.");
          callback(null, { status: 200, data: {} });
          return;
        }
        let _url = resources.hasOwnProperty(url) ? url : "en";
        // Bundled src/locales/<lng>/translation.json ships with ~90%+ of keys already
        // translated (verified against IL_Translations coverage, which is much sparser
        // for most languages) but was never actually used at runtime — start from it as
        // the base layer so a key missing from the backend table falls back to its real
        // translation instead of straight to the raw English key (useKeysAsDefaultValue).
        // Backend rows still override the bundle where present, so per-org customization
        // via IL_Translations keeps working exactly as before.
        const flatTranslations = { ...(resources[_url]?.translation || {}) };
        const e2Data = new E2Data();
        e2Data.entity = "IL_Translations";
        // Active = true — the table also holds inactive placeholder rows (Value equal to
        // Key, i.e. never actually translated) that were previously being merged in
        // anyway, silently overriding the bundled fallback above with untranslated
        // English text for any key that happened to have one of these stub rows.
        e2Data.filter = `Language = '${_url}' AND Active = true`;
        e2Data.take = 100000;
        e2Data.GetData().then((data) => {
          //console.log(data);
          if (data && data.result && Array.isArray(data.result.data) && data.result.data.length > 0) {
            for (const entry of data.result.data) {
              const key = entry.Key;
              const value = entry.Value;

              if (
                entry.Active !== false &&
                typeof key === 'string' &&
                key.trim() !== '' &&
                typeof value === 'string' &&
                value.trim() !== ''
              ) {
                flatTranslations[key] = value;
              }
            }
          } else {
            console.log("Unexpected response:", data);
          }
          callback(null, { status: 200, data: flatTranslations });
        })
      }
    },
    //saveMissing: true,
    returnEmptyString :false,
  });


const loadBasePath = () => {
  let availableBasePaths = ["integration", "publicpresentation","public-api-playground", "administration", "flow", "mycompany", "packages", "dist-channels"];
  let pathNames = window.location?.pathname?.replace(/^\/|\/$/g, '').split("/") || [];
  if(pathNames.length === 0)
    return;

  //console.log(pathNames);
  if(availableBasePaths.includes(pathNames[0].toLocaleLowerCase()))
    return pathNames[0].toLocaleLowerCase();
}
const queryClient = new QueryClient()

root.render(
  <BrowserRouter basename={loadBasePath()}>
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
      <WebsocketProvider>
      <BasepathContextProvider>
      {(loadBasePath() === "integration" || loadBasePath() === "publicpresentation" || loadBasePath() === "public-api-playground") && <>
        <OrgIdContextProvider>
        <ThemeProvider>
          <IntegrationApp/>
        </ThemeProvider>
        </OrgIdContextProvider>
      </>}
      {(loadBasePath() !== "integration" && loadBasePath() !== "publicpresentation" && loadBasePath() !== "public-api-playground") && <>
      <OrgIdContextProvider>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </OrgIdContextProvider>
      </>}
      </BasepathContextProvider>
      </WebsocketProvider>
      </QueryClientProvider>
    </React.StrictMode>
  </BrowserRouter>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
